/**
 * Access Management Page Component
 * Admin page for managing access requests, organizations, and email rules
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Button, Card, Message, Input } from '../../components/ui';
import InvitePanel from '../../components/design-check/InvitePanel';
import AgreementPanel from '../support/AgreementPanel';
import {
  INTERNAL_EMAIL_DOMAIN,
  roleLabel,
  roleChoices,
  openingRole,
  isInternalRole,
  isInternalEmail,
  sendWithOutsideEmailConfirm,
} from './rolePolicy';
import './AccessManagement.css';

function foldSearchText(value) {
  return String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
}

function looksLikeEmail(value) {
  const text = String(value || '').trim();
  return text.includes('@') && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text);
}

function displayUserName(user) {
  const first = String(user && user.firstName ? user.firstName : '').trim();
  const last = String(user && user.lastName ? user.lastName : '').trim();
  if (first && last) return `${first} ${last}`;
  if (first) return first;
  return user && user.username ? user.username : '';
}

function displayUserEmail(user) {
  const email = String(user && user.email ? user.email : '').trim();
  if (email) return email;
  if (looksLikeEmail(user && user.username)) return user.username;
  return '';
}

function createTemporaryPassword() {
  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const numbers = '0123456789';
  const special = '!@#$%';
  const all = lowercase + uppercase + numbers + special;
  const bytes = new Uint32Array(12);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 0xffffffff);
  }
  const pick = (chars, index) => chars[bytes[index] % chars.length];
  const chars = [pick(lowercase, 0), pick(uppercase, 1), pick(numbers, 2), pick(special, 3)];
  for (let i = 4; i < 12; i += 1) chars.push(pick(all, i));
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const swapWith = bytes[i] % (i + 1);
    const held = chars[i];
    chars[i] = chars[swapWith];
    chars[swapWith] = held;
  }
  return chars.join('');
}

function organisationChoices(organizations, current) {
  const names = (organizations || [])
    .filter((org) => org && org.name && org.isActive !== false)
    .map((org) => org.name);
  const currentName = String(current || '').trim();
  if (currentName && !names.some((name) => name.toLowerCase() === currentName.toLowerCase())) {
    names.push(currentName);
  }
  return names.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

function userMatchesSearch(user, query) {
  const raw = String(query || '').trim().toLowerCase();
  if (!raw) return true;
  const folded = foldSearchText(raw);
  const haystacks = [
    user.username,
    user.email,
    user.firstName,
    user.lastName,
    `${user.firstName || ''} ${user.lastName || ''}`,
    `${user.lastName || ''} ${user.firstName || ''}`,
    user.organization,
    user.role,
  ];
  return haystacks.some((field) => {
    const text = String(field || '').toLowerCase();
    return text.includes(raw) || (folded && foldSearchText(field).includes(folded));
  });
}

const AccessManagement = ({ userRole }) => {
  const isAdmin = userRole === 'admin';
  const [activeTab, setActiveTab] = useState(isAdmin ? 'requests' : 'users');
  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [emailRules, setEmailRules] = useState(null);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [approvalModal, setApprovalModal] = useState(null);
  const [newOrgModal, setNewOrgModal] = useState(false);
  const [editOrgModal, setEditOrgModal] = useState(null);
  const [editUserModal, setEditUserModal] = useState(null);
  const [newUserModal, setNewUserModal] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [orgModalForUser, setOrgModalForUser] = useState(false);
  const [pickedOrganization, setPickedOrganization] = useState('');
  const [agreementOrg, setAgreementOrg] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  };

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch('/api/admin/access-stats', {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      } else {
        console.error('Failed to fetch stats:', data.error);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  const fetchRequests = useCallback(async () => {
    try {
      const response = await fetch(`/api/admin/access-requests?status=${statusFilter}`, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        setRequests(data.requests);
      } else {
        showMessage(data.error || 'Failed to fetch access requests', 'error');
      }
    } catch (error) {
      showMessage('Failed to fetch access requests', 'error');
    }
  }, [statusFilter]);

  const fetchOrganizations = useCallback(async () => {
    try {
      const response = await fetch('/api/admin/organizations', {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        setOrganizations(data.organizations);
      } else {
        showMessage(data.error || 'Failed to fetch organizations', 'error');
      }
    } catch (error) {
      showMessage('Failed to fetch organizations', 'error');
    }
  }, []);

  const fetchEmailRules = useCallback(async () => {
    try {
      const response = await fetch('/api/admin/email-rules', {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        setEmailRules(data.rules);
      } else {
        showMessage(data.error || 'Failed to fetch email rules', 'error');
      }
    } catch (error) {
      showMessage('Failed to fetch email rules', 'error');
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch('/api/admin/users', {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        // Convert users object to array with username as property
        const usersArray = Object.entries(data.users).map(([username, userData]) => ({
          username,
          ...userData,
        }));
        usersArray.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        setUsers(usersArray);
      } else {
        showMessage(data.error || 'Failed to fetch users', 'error');
      }
    } catch (error) {
      showMessage('Failed to fetch users', 'error');
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      if (isAdmin) {
        await Promise.all([fetchStats(), fetchRequests(), fetchOrganizations(), fetchEmailRules(), fetchUsers()]);
      } else {
        await Promise.all([fetchUsers(), fetchOrganizations()]);
      }
      setIsLoading(false);
    };
    loadData();
  }, [isAdmin, fetchStats, fetchRequests, fetchOrganizations, fetchEmailRules, fetchUsers]);

  useEffect(() => {
    if (activeTab === 'requests') {
      fetchRequests();
    }
  }, [statusFilter, activeTab, fetchRequests]);

  const handleApprove = async (request, options = {}) => {
    try {
      const data = await sendWithOutsideEmailConfirm(async (payload) => {
        const response = await fetch(`/api/admin/access-requests/${request.id}/approve`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(payload),
        });
        return response.json();
      }, options);
      if (data.success) {
        showMessage(`Access approved as ${roleLabel(data.role)}. Username: ${data.username}, Temp Password: ${data.tempPassword}`, 'success');
        setApprovalModal({ ...data, request });
        fetchRequests();
        fetchStats();
      } else {
        showMessage(data.error || 'Failed to approve request', 'error');
      }
    } catch (error) {
      showMessage('Failed to approve request', 'error');
    }
  };

  const handleReject = async (request, reason) => {
    try {
      const response = await fetch(`/api/admin/access-requests/${request.id}/reject`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ reason }),
      });
      const data = await response.json();
      if (data.success) {
        showMessage('Request rejected', 'success');
        setSelectedRequest(null);
        fetchRequests();
        fetchStats();
      } else {
        showMessage(data.error || 'Failed to reject request', 'error');
      }
    } catch (error) {
      showMessage('Failed to reject request', 'error');
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this request?')) return;
    try {
      const response = await fetch(`/api/admin/access-requests/${requestId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        showMessage('Request deleted', 'success');
        fetchRequests();
        fetchStats();
      } else {
        showMessage(data.error || 'Failed to delete request', 'error');
      }
    } catch (error) {
      showMessage('Failed to delete request', 'error');
    }
  };

  const handleSaveOrganization = async (orgData, isEdit = false) => {
    try {
      const url = isEdit
        ? `/api/admin/organizations/${orgData.id}`
        : '/api/admin/organizations';
      const method = isEdit ? 'PATCH' : 'POST';

      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(orgData),
      });
      const data = await response.json();
      if (data.success) {
        showMessage(isEdit ? 'Organization updated' : 'Organization created', 'success');
        setNewOrgModal(false);
        setEditOrgModal(null);
        if (!isEdit && data.organization && data.organization.name) {
          setOrganizations((current) => {
            const name = String(data.organization.name).toLowerCase();
            if (current.some((org) => String(org.name || '').toLowerCase() === name)) return current;
            return [...current, data.organization];
          });
        }
        fetchOrganizations();
        fetchEmailRules();
        return data.organization || null;
      }
      showMessage(data.error || 'Failed to save organization', 'error');
      return null;
    } catch (error) {
      showMessage('Failed to save organization', 'error');
      return null;
    }
  };

  const handleDeleteOrganization = async (orgId) => {
    if (!window.confirm('Are you sure you want to delete this organization?')) return;
    try {
      const response = await fetch(`/api/admin/organizations/${orgId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        showMessage('Organization deleted', 'success');
        fetchOrganizations();
        fetchEmailRules();
      } else {
        showMessage(data.error || 'Failed to delete organization', 'error');
      }
    } catch (error) {
      showMessage('Failed to delete organization', 'error');
    }
  };

  const handleUpdateEmailRules = async (updates) => {
    try {
      const response = await fetch('/api/admin/email-rules', {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(updates),
      });
      const data = await response.json();
      if (data.success) {
        showMessage('Email rules updated', 'success');
        setEmailRules(data.rules);
      } else {
        showMessage(data.error || 'Failed to update email rules', 'error');
      }
    } catch (error) {
      showMessage('Failed to update email rules', 'error');
    }
  };

  // User Management Handlers
  const handleCreateUser = async (userData) => {
    try {
      const data = await sendWithOutsideEmailConfirm(async (payload) => {
        const response = await fetch('/api/admin/create-user', {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(payload),
        });
        return response.json();
      }, userData);
      if (data.success) {
        setNewUserModal(false);
        setPickedOrganization('');
        setUserSearch('');
        fetchUsers();
        fetchStats();
        if (userData.requirePasswordChange && userData.password) {
          setApprovalModal({
            heading: 'User created',
            note: 'Share this temporary password. They must change it the first time they sign in.',
            email: userData.email,
            username: data.username || userData.username,
            tempPassword: userData.password,
          });
        } else {
          showMessage(`User ${data.username || userData.username} created successfully`, 'success');
        }
      } else {
        showMessage(data.error || 'Failed to create user', 'error');
      }
      return data;
    } catch (error) {
      showMessage('Failed to create user', 'error');
      return { success: false, error: 'Failed to create user' };
    }
  };

  const handleUpdateUser = async (username, updates) => {
    try {
      const data = await sendWithOutsideEmailConfirm(async (payload) => {
        const response = await fetch(`/api/admin/users/${encodeURIComponent(username)}`, {
          method: 'PATCH',
          headers: getAuthHeaders(),
          body: JSON.stringify(payload),
        });
        return response.json();
      }, updates);
      if (data.success) {
        const edited = editUserModal;
        setEditUserModal(null);
        setPickedOrganization('');
        fetchUsers();
        if (updates.requirePasswordChange && updates.password) {
          setApprovalModal({
            heading: 'Temporary password set',
            note: 'Share this password. They must change it the first time they sign in.',
            email: updates.email || (edited && edited.email) || '',
            username: edited ? edited.username : '',
            tempPassword: updates.password,
          });
        } else {
          showMessage('User updated successfully', 'success');
        }
      } else {
        showMessage(data.error || 'Failed to update user', 'error');
      }
      return data;
    } catch (error) {
      showMessage('Failed to update user', 'error');
      return { success: false, error: 'Failed to update user' };
    }
  };

  const handleDeleteUser = async (username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"? This action cannot be undone.`)) return;
    try {
      const response = await fetch(`/api/admin/users/${encodeURIComponent(username)}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        showMessage(`User ${username} deleted`, 'success');
        fetchUsers();
        fetchStats();
      } else {
        showMessage(data.error || 'Failed to delete user', 'error');
      }
    } catch (error) {
      showMessage('Failed to delete user', 'error');
    }
  };

  // Filter users based on search
  const filteredUsers = users.filter((user) => userMatchesSearch(user, userSearch));

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  if (isLoading) {
    return (
      <div className="rf-access-management">
        <div className="rf-loading">
          <i className="fas fa-spinner fa-spin"></i>
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="rf-access-management">
      <div className="rf-page-header">
        <h1><i className="fas fa-shield-alt"></i> {isAdmin ? 'Access Management' : 'Users'}</h1>
        <p>{isAdmin ? 'Manage access requests, organizations, and email rules' : 'Add a customer account and find them by name, email, or organisation'}</p>
      </div>

      {message.text && <Message type={message.type}>{message.text}</Message>}

      {/* Stats Overview */}
      {isAdmin && stats && (
        <div className="rf-stats-grid">
          <Card className="rf-stat-card">
            <div className="rf-stat-icon rf-pending">
              <i className="fas fa-clock"></i>
            </div>
            <div className="rf-stat-content">
              <span className="rf-stat-number">{stats.requests.pending}</span>
              <span className="rf-stat-label">Pending Requests</span>
            </div>
          </Card>
          <Card className="rf-stat-card">
            <div className="rf-stat-icon rf-success">
              <i className="fas fa-users"></i>
            </div>
            <div className="rf-stat-content">
              <span className="rf-stat-number">{stats.users.total}</span>
              <span className="rf-stat-label">Total Users</span>
            </div>
          </Card>
          <Card className="rf-stat-card">
            <div className="rf-stat-icon rf-info">
              <i className="fas fa-building"></i>
            </div>
            <div className="rf-stat-content">
              <span className="rf-stat-number">{stats.organizations.total}</span>
              <span className="rf-stat-label">Organizations</span>
            </div>
          </Card>
          <Card className="rf-stat-card">
            <div className="rf-stat-icon rf-primary">
              <i className="fas fa-globe"></i>
            </div>
            <div className="rf-stat-content">
              <span className="rf-stat-number">{stats.emailRules.allowedDomains}</span>
              <span className="rf-stat-label">Allowed Domains</span>
            </div>
          </Card>
        </div>
      )}

      {/* Tab Navigation */}
      {isAdmin && <div className="rf-tabs">
        <button
          className={`rf-tab ${activeTab === 'requests' ? 'active' : ''}`}
          onClick={() => setActiveTab('requests')}
        >
          <i className="fas fa-inbox"></i>
          Access Requests
          {stats?.requests.pending > 0 && (
            <span className="rf-badge">{stats.requests.pending}</span>
          )}
        </button>
        <button
          className={`rf-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <i className="fas fa-users-cog"></i>
          Users
          <span className="rf-count-light">{users.length}</span>
        </button>
        <button
          className={`rf-tab ${activeTab === 'organizations' ? 'active' : ''}`}
          onClick={() => setActiveTab('organizations')}
        >
          <i className="fas fa-building"></i>
          Organizations
        </button>
        <button
          className={`rf-tab ${activeTab === 'email-rules' ? 'active' : ''}`}
          onClick={() => setActiveTab('email-rules')}
        >
          <i className="fas fa-envelope-open-text"></i>
          Email Rules
        </button>
        <button
          className={`rf-tab ${activeTab === 'design-check' ? 'active' : ''}`}
          onClick={() => setActiveTab('design-check')}
        >
          <i className="fas fa-cube"></i>
          Design Check
        </button>
      </div>}

      {/* Access Requests Tab */}
      {activeTab === 'requests' && (
        <Card className="rf-tab-content">
          <div className="rf-filter-bar">
            <div className="rf-status-filters">
              {['pending', 'approved', 'rejected', 'auto-approved'].map((status) => (
                <button
                  key={status}
                  className={`rf-filter-btn ${statusFilter === status ? 'active' : ''}`}
                  onClick={() => setStatusFilter(status)}
                >
                  {status.replace('-', ' ')}
                  {stats?.requests[status === 'auto-approved' ? 'autoApproved' : status] > 0 && (
                    <span className="rf-count">
                      {stats.requests[status === 'auto-approved' ? 'autoApproved' : status]}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {requests.length === 0 ? (
            <div className="rf-empty-state">
              <i className="fas fa-inbox"></i>
              <p>No {statusFilter} requests found</p>
            </div>
          ) : (
            <div className="rf-requests-list">
              {requests.map((request) => (
                <div key={request.id} className="rf-request-item">
                  <div className="rf-request-avatar">
                    {request.firstName?.charAt(0)}{request.lastName?.charAt(0)}
                  </div>
                  <div className="rf-request-details">
                    <div className="rf-request-name">
                      {request.firstName} {request.lastName}
                    </div>
                    <div className="rf-request-email">{request.email}</div>
                    {request.organization && (
                      <div className="rf-request-org">
                        <i className="fas fa-building"></i> {request.organization}
                      </div>
                    )}
                    <div className="rf-request-date">
                      <i className="fas fa-clock"></i> {formatDate(request.submittedAt)}
                    </div>
                  </div>
                  <div className="rf-request-actions">
                    {request.status === 'pending' ? (
                      <>
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => handleApprove(request)}
                        >
                          <i className="fas fa-check"></i> Approve
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => setSelectedRequest(request)}
                        >
                          <i className="fas fa-times"></i> Reject
                        </Button>
                      </>
                    ) : (
                      <>
                        <span className={`rf-status-badge rf-status-${request.status}`}>
                          {request.status}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteRequest(request.id)}
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <Card className="rf-tab-content">
          <div className="rf-section-header">
            <h3>Manage Users</h3>
            <Button variant="primary" onClick={() => setNewUserModal(true)}>
              <i className="fas fa-user-plus"></i> Add User
            </Button>
          </div>

          <div className="rf-search-bar">
            <i className="fas fa-search"></i>
            <input
              type="text"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              placeholder="Search by name, email, organisation, or role"
              className="rf-search-input"
            />
            {userSearch && (
              <button className="rf-search-clear" onClick={() => setUserSearch('')}>
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>

          {filteredUsers.length === 0 ? (
            <div className="rf-empty-state">
              <i className="fas fa-users"></i>
              <p>{userSearch ? 'No users match your search' : 'No users found'}</p>
            </div>
          ) : (
            <div className="rf-users-table-container">
              <table className="rf-users-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Organization</th>
                    <th>Role</th>
                    <th>AI Access</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.username}>
                      <td>
                        <div className="rf-user-cell">
                          <div className="rf-user-avatar-sm">
                            {displayUserName(user).charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="rf-user-name">{displayUserName(user)}</div>
                            {!looksLikeEmail(user.username) && (
                              <div className="rf-user-username">@{user.username}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>{displayUserEmail(user) || '-'}</td>
                      <td>{user.organization || '-'}</td>
                      <td>
                        <span className={`rf-role-badge rf-role-${user.role || 'external'}`}>
                          {roleLabel(user.role)}
                        </span>
                        {user.resellerAssetsEditor === true && ['sales', 'user'].includes(user.role) && (
                          <span className="rf-ai-badge rf-ai-enabled" title="Can edit Reseller Assets" style={{ marginLeft: 6 }}>
                            assets editor
                          </span>
                        )}
                      </td>
                      <td>
                        <span className={`rf-ai-badge rf-ai-${user.aiRole || 'enabled'}`}>
                          {user.aiRole || 'enabled'}
                        </span>
                      </td>
                      <td>
                        <div className="rf-table-actions">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditUserModal(user)}
                            title="Edit name, handle, and organisation"
                          >
                            <i className="fas fa-edit"></i>
                            Edit
                          </Button>
                          {isAdmin && (
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDeleteUser(user.username)}
                            title="Delete user"
                          >
                            <i className="fas fa-trash"></i>
                          </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="rf-users-summary">
            <span><strong>Total:</strong> {users.length} users</span>
            {isAdmin && stats?.users?.byRole && (
              <>
                <span><i className="fas fa-user-shield"></i> {stats.users.byRole.admin} admins</span>
                <span><i className="fas fa-chart-line"></i> {stats.users.byRole.sales} sales</span>
                <span><i className="fas fa-user"></i> {stats.users.byRole.user} users</span>
                <span><i className="fas fa-store"></i> {stats.users.byRole.reseller} resellers</span>
              </>
            )}
          </div>
        </Card>
      )}

      {/* Organizations Tab */}
      {activeTab === 'organizations' && (
        <Card className="rf-tab-content">
          <div className="rf-section-header">
            <h3>Organizations</h3>
            <Button variant="primary" onClick={() => setNewOrgModal(true)}>
              <i className="fas fa-plus"></i> Add Organization
            </Button>
          </div>

          {organizations.length === 0 ? (
            <div className="rf-empty-state">
              <i className="fas fa-building"></i>
              <p>No organizations configured</p>
              <Button variant="primary" onClick={() => setNewOrgModal(true)}>
                Create First Organization
              </Button>
            </div>
          ) : (
            <div className="rf-org-list">
              {organizations.map((org) => (
                <div key={org.id} className="rf-org-item">
                  <div className="rf-org-header">
                    <div className="rf-org-name">
                      <i className="fas fa-building"></i>
                      {org.name}
                      {!org.isActive && <span className="rf-inactive-badge">Inactive</span>}
                      {org.earlyAccess && <span className="rf-early-access-badge">Early access</span>}
                    </div>
                    <div className="rf-org-actions">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setAgreementOrg(agreementOrg && agreementOrg.id === org.id ? null : org)}
                      >
                        Service agreement
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditOrgModal(org)}
                        title="Edit organisation"
                        aria-label="Edit organisation"
                      >
                        <i className="fas fa-edit"></i>
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDeleteOrganization(org.id)}
                      >
                        <i className="fas fa-trash"></i>
                      </Button>
                    </div>
                  </div>
                  <div className="rf-org-details">
                    {org.domains?.length > 0 && (
                      <div className="rf-org-domains">
                        <strong>Domains:</strong>
                        {org.domains.map((d) => (
                          <span key={d} className="rf-domain-tag">{d}</span>
                        ))}
                      </div>
                    )}
                    <div className="rf-org-meta">
                      <span><i className="fas fa-users"></i> {org.userCount || 0} users</span>
                      <span><i className="fas fa-user-tag"></i> Default: {roleLabel(org.defaultRole)}</span>
                      {typeof org.resellerDiscount === 'number' && (
                        <span><i className="fas fa-percent"></i> Reseller discount: {Math.round(org.resellerDiscount * 10000) / 100}%</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {agreementOrg && (
            <AgreementPanel orgId={agreementOrg.id} orgName={agreementOrg.name} />
          )}
        </Card>
      )}

      {/* Design Check Tab */}
      {activeTab === 'design-check' && (
        <InvitePanel showMessage={showMessage} />
      )}

      {/* Email Rules Tab */}
      {activeTab === 'email-rules' && emailRules && (
        <Card className="rf-tab-content">
          <EmailRulesEditor
            rules={emailRules}
            onSave={handleUpdateEmailRules}
          />
        </Card>
      )}

      {/* Rejection Modal */}
      {selectedRequest && (
        <RejectModal
          request={selectedRequest}
          onReject={handleReject}
          onClose={() => setSelectedRequest(null)}
        />
      )}

      {/* Approval Success Modal */}
      {approvalModal && (
        <ApprovalSuccessModal
          data={approvalModal}
          onClose={() => setApprovalModal(null)}
        />
      )}

      {/* New Organization Modal */}
      {newOrgModal && (
        <OrganizationModal
          onSave={handleSaveOrganization}
          onClose={() => setNewOrgModal(false)}
        />
      )}

      {/* Edit Organization Modal */}
      {editOrgModal && (
        <OrganizationModal
          organization={editOrgModal}
          onSave={(data) => handleSaveOrganization({ ...data, id: editOrgModal.id }, true)}
          onClose={() => setEditOrgModal(null)}
        />
      )}

      {/* New User Modal */}
      {newUserModal && (
        <UserModal
          organizations={organizations}
          canCreateAdmin={isAdmin}
          canEditPrivileges={isAdmin}
          canCreateOrganization={isAdmin}
          pickedOrganization={pickedOrganization}
          onRequestNewOrganization={() => setOrgModalForUser(true)}
          onSave={handleCreateUser}
          onClose={() => {
            setNewUserModal(false);
            setPickedOrganization('');
          }}
        />
      )}

      {/* Edit User Modal */}
      {editUserModal && (
        <UserModal
          user={editUserModal}
          organizations={organizations}
          canCreateAdmin={isAdmin}
          canEditPrivileges={isAdmin}
          canCreateOrganization={isAdmin}
          pickedOrganization={pickedOrganization}
          onRequestNewOrganization={() => setOrgModalForUser(true)}
          onSave={(data) => handleUpdateUser(editUserModal.username, data)}
          onClose={() => {
            setEditUserModal(null);
            setPickedOrganization('');
          }}
        />
      )}

      {orgModalForUser && (
        <OrganizationModal
          onSave={async (data) => {
            const created = await handleSaveOrganization(data);
            if (created && created.name) {
              setPickedOrganization(created.name);
              setOrgModalForUser(false);
            }
          }}
          onClose={() => setOrgModalForUser(false)}
        />
      )}
    </div>
  );
};

// Rejection Modal Component
const RejectModal = ({ request, onReject, onClose }) => {
  const [reason, setReason] = useState('');

  return (
    <div className="rf-modal-overlay">
      <div className="rf-modal">
        <div className="rf-modal-header">
          <h3>Reject Access Request</h3>
          <button className="rf-modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="rf-modal-body">
          <p>
            Rejecting request from <strong>{request.firstName} {request.lastName}</strong> ({request.email})
          </p>
          <div className="rf-form-group">
            <label>Reason for rejection:</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason for rejection..."
              rows={3}
              className="rf-textarea"
            />
          </div>
        </div>
        <div className="rf-modal-footer">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="danger" onClick={() => onReject(request, reason)}>
            Reject Request
          </Button>
        </div>
      </div>
    </div>
  );
};

// Approval Success Modal Component
const ApprovalSuccessModal = ({ data, onClose }) => {
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="rf-modal-overlay">
      <div className="rf-modal">
        <div className="rf-modal-header rf-success-header">
          <h3><i className="fas fa-check-circle"></i> {data.heading || 'Access Approved'}</h3>
          <button className="rf-modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="rf-modal-body">
          <p>{data.note || 'Access has been granted. Share these credentials with the user:'}</p>
          <div className="rf-credentials-display">
            <div className="rf-credential-row">
              <span className="rf-label">Email:</span>
              <span className="rf-value">{data.email}</span>
            </div>
            <div className="rf-credential-row">
              <span className="rf-label">Username:</span>
              <span className="rf-value">{data.username}</span>
              <button
                className="rf-copy-btn"
                onClick={() => copyToClipboard(data.username)}
              >
                <i className="fas fa-copy"></i>
              </button>
            </div>
            <div className="rf-credential-row">
              <span className="rf-label">Temp Password:</span>
              <span className="rf-value rf-password">{data.tempPassword}</span>
              <button
                className="rf-copy-btn"
                onClick={() => copyToClipboard(data.tempPassword)}
              >
                <i className="fas fa-copy"></i>
              </button>
            </div>
          </div>
          <div className="rf-copy-all">
            <Button
              variant="primary"
              onClick={() => copyToClipboard(
                `Username: ${data.username}\nPassword: ${data.tempPassword}`
              )}
            >
              <i className="fas fa-copy"></i> Copy All Credentials
            </Button>
          </div>
        </div>
        <div className="rf-modal-footer">
          <Button variant="success" onClick={onClose}>Done</Button>
        </div>
      </div>
    </div>
  );
};

// Organization Modal Component
const OrganizationModal = ({ organization, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: organization?.name || '',
    domains: organization?.domains?.join(', ') || '',
    defaultRole: organization?.defaultRole || 'external',
    defaultAiRole: organization?.defaultAiRole || 'enabled',
    maxUsers: organization?.maxUsers || '',
    notes: organization?.notes || '',
    isActive: organization?.isActive !== false,
    earlyAccess: organization?.earlyAccess === true,
    resellerDiscountPct: typeof organization?.resellerDiscount === 'number'
      ? String(Math.round(organization.resellerDiscount * 10000) / 100)
      : '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const domains = formData.domains
      .split(',')
      .map((d) => d.trim().toLowerCase())
      .filter(Boolean);
    const { resellerDiscountPct, ...fields } = formData;
    onSave({
      ...fields,
      domains,
      maxUsers: formData.maxUsers ? parseInt(formData.maxUsers) : null,
      resellerDiscount: resellerDiscountPct.trim() === '' ? null : Number(resellerDiscountPct) / 100,
    });
  };

  return (
    <div className="rf-modal-overlay">
      <div className="rf-modal rf-modal-lg">
        <div className="rf-modal-header">
          <h3>{organization ? 'Edit Organization' : 'Add Organization'}</h3>
          <button className="rf-modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="rf-modal-body">
            <Input
              label="Organization Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="e.g., Acme Corporation"
            />

            <div className="rf-form-group">
              <label className="rf-input-label">
                Email Domains (comma separated)
              </label>
              <input
                type="text"
                value={formData.domains}
                onChange={(e) => setFormData({ ...formData, domains: e.target.value })}
                placeholder="e.g., acme.com, acme.co.uk"
                className="rf-input"
              />
              <small className="rf-help-text">
                Users from these domains will be auto-approved
              </small>
            </div>

            <div className="rf-form-row">
              <div className="rf-form-group">
                <label className="rf-input-label">Default Role</label>
                <select
                  value={formData.defaultRole}
                  onChange={(e) => setFormData({ ...formData, defaultRole: e.target.value })}
                  className="rf-select"
                >
                  {roleChoices().map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div className="rf-form-group">
                <label className="rf-input-label">Default AI Role</label>
                <select
                  value={formData.defaultAiRole}
                  onChange={(e) => setFormData({ ...formData, defaultAiRole: e.target.value })}
                  className="rf-select"
                >
                  <option value="enabled">Enabled</option>
                  <option value="disabled">Disabled</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>

            <Input
              label="Max Users (optional)"
              type="number"
              value={formData.maxUsers}
              onChange={(e) => setFormData({ ...formData, maxUsers: e.target.value })}
              placeholder="Leave empty for unlimited"
            />

            <Input
              label="Reseller discount % (optional)"
              type="number"
              min="0"
              max="99"
              step="0.5"
              value={formData.resellerDiscountPct}
              onChange={(e) => setFormData({ ...formData, resellerDiscountPct: e.target.value })}
              placeholder="Leave empty to use the price list default"
            />

            <div className="rf-form-group">
              <label className="rf-input-label">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Internal notes about this organization..."
                rows={2}
                className="rf-textarea"
              />
            </div>

            <div className="rf-form-group rf-checkbox-group">
              <label className="rf-checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
                <span>Organization is active</span>
              </label>
            </div>

            <div className="rf-form-group rf-checkbox-group">
              <label className="rf-checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.earlyAccess}
                  onChange={(e) => setFormData({ ...formData, earlyAccess: e.target.checked })}
                />
                <span>Early access to pre-release training</span>
              </label>
            </div>
          </div>
          <div className="rf-modal-footer">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="primary">
              {organization ? 'Update' : 'Create'} Organization
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Email Rules Editor Component
const EmailRulesEditor = ({ rules, onSave }) => {
  const [newDomain, setNewDomain] = useState('');
  const [newBlockedDomain, setNewBlockedDomain] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newBlockedEmail, setNewBlockedEmail] = useState('');
  const [localRules, setLocalRules] = useState(rules);

  const addToList = (list, value, setValue) => {
    if (!value.trim()) return;
    const newValue = value.toLowerCase().trim();
    if (localRules[list].includes(newValue)) return;
    const updated = { ...localRules, [list]: [...localRules[list], newValue] };
    setLocalRules(updated);
    onSave({ [list]: updated[list] });
    setValue('');
  };

  const removeFromList = (list, value) => {
    const updated = { ...localRules, [list]: localRules[list].filter((v) => v !== value) };
    setLocalRules(updated);
    onSave({ [list]: updated[list] });
  };

  const toggleRequireApproval = () => {
    const updated = { ...localRules, requireApproval: !localRules.requireApproval };
    setLocalRules(updated);
    onSave({ requireApproval: updated.requireApproval });
  };

  return (
    <div className="rf-email-rules">
      <div className="rf-rules-section">
        <h4><i className="fas fa-check-circle"></i> Auto-Approve Settings</h4>
        <div className="rf-toggle-setting">
          <label className="rf-toggle">
            <input
              type="checkbox"
              checked={!localRules.requireApproval}
              onChange={toggleRequireApproval}
            />
            <span className="rf-toggle-slider"></span>
          </label>
          <span>Auto-approve all requests (no admin review needed)</span>
        </div>
      </div>

      <div className="rf-rules-columns">
        <div className="rf-rules-column">
          <h4><i className="fas fa-shield-alt"></i> Allowed Domains</h4>
          <p className="rf-help">Requests from these domains are auto-approved</p>
          <div className="rf-add-row">
            <input
              type="text"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              placeholder="e.g., company.com"
              className="rf-input"
              onKeyPress={(e) => e.key === 'Enter' && addToList('allowedDomains', newDomain, setNewDomain)}
            />
            <Button
              variant="primary"
              size="sm"
              onClick={() => addToList('allowedDomains', newDomain, setNewDomain)}
            >
              <i className="fas fa-plus"></i>
            </Button>
          </div>
          <div className="rf-tag-list">
            {localRules.allowedDomains.map((domain) => (
              <span key={domain} className="rf-tag rf-tag-success">
                {domain}
                <button onClick={() => removeFromList('allowedDomains', domain)}>
                  <i className="fas fa-times"></i>
                </button>
              </span>
            ))}
          </div>
        </div>

        <div className="rf-rules-column">
          <h4><i className="fas fa-ban"></i> Blocked Domains</h4>
          <p className="rf-help">Requests from these domains are automatically rejected</p>
          <div className="rf-add-row">
            <input
              type="text"
              value={newBlockedDomain}
              onChange={(e) => setNewBlockedDomain(e.target.value)}
              placeholder="e.g., spam.com"
              className="rf-input"
              onKeyPress={(e) => e.key === 'Enter' && addToList('blockedDomains', newBlockedDomain, setNewBlockedDomain)}
            />
            <Button
              variant="danger"
              size="sm"
              onClick={() => addToList('blockedDomains', newBlockedDomain, setNewBlockedDomain)}
            >
              <i className="fas fa-plus"></i>
            </Button>
          </div>
          <div className="rf-tag-list">
            {localRules.blockedDomains.map((domain) => (
              <span key={domain} className="rf-tag rf-tag-danger">
                {domain}
                <button onClick={() => removeFromList('blockedDomains', domain)}>
                  <i className="fas fa-times"></i>
                </button>
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="rf-rules-columns">
        <div className="rf-rules-column">
          <h4><i className="fas fa-user-check"></i> Pre-Approved Emails</h4>
          <p className="rf-help">These specific emails are auto-approved</p>
          <div className="rf-add-row">
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="e.g., vip@example.com"
              className="rf-input"
              onKeyPress={(e) => e.key === 'Enter' && addToList('allowedEmails', newEmail, setNewEmail)}
            />
            <Button
              variant="primary"
              size="sm"
              onClick={() => addToList('allowedEmails', newEmail, setNewEmail)}
            >
              <i className="fas fa-plus"></i>
            </Button>
          </div>
          <div className="rf-tag-list">
            {localRules.allowedEmails.map((email) => (
              <span key={email} className="rf-tag rf-tag-success">
                {email}
                <button onClick={() => removeFromList('allowedEmails', email)}>
                  <i className="fas fa-times"></i>
                </button>
              </span>
            ))}
          </div>
        </div>

        <div className="rf-rules-column">
          <h4><i className="fas fa-user-times"></i> Blocked Emails</h4>
          <p className="rf-help">These specific emails cannot request access</p>
          <div className="rf-add-row">
            <input
              type="email"
              value={newBlockedEmail}
              onChange={(e) => setNewBlockedEmail(e.target.value)}
              placeholder="e.g., blocked@example.com"
              className="rf-input"
              onKeyPress={(e) => e.key === 'Enter' && addToList('blockedEmails', newBlockedEmail, setNewBlockedEmail)}
            />
            <Button
              variant="danger"
              size="sm"
              onClick={() => addToList('blockedEmails', newBlockedEmail, setNewBlockedEmail)}
            >
              <i className="fas fa-plus"></i>
            </Button>
          </div>
          <div className="rf-tag-list">
            {localRules.blockedEmails.map((email) => (
              <span key={email} className="rf-tag rf-tag-danger">
                {email}
                <button onClick={() => removeFromList('blockedEmails', email)}>
                  <i className="fas fa-times"></i>
                </button>
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="rf-rules-section">
        <h4><i className="fas fa-cog"></i> Default Settings for New Users</h4>
        <div className="rf-default-settings">
          <div className="rf-form-group">
            <label>Default Role</label>
            <select
              value={localRules.defaultRole}
              onChange={(e) => {
                const updated = { ...localRules, defaultRole: e.target.value };
                setLocalRules(updated);
                onSave({ defaultRole: e.target.value });
              }}
              className="rf-select"
            >
              {roleChoices().map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <small className="rf-help-text">
              Rapid Fusion roles are only given to @{INTERNAL_EMAIL_DOMAIN} addresses. Everyone else gets External.
            </small>
          </div>
          <div className="rf-form-group">
            <label>Default AI Role</label>
            <select
              value={localRules.defaultAiRole}
              onChange={(e) => {
                const updated = { ...localRules, defaultAiRole: e.target.value };
                setLocalRules(updated);
                onSave({ defaultAiRole: e.target.value });
              }}
              className="rf-select"
            >
              <option value="enabled">Enabled</option>
              <option value="disabled">Disabled</option>
              <option value="admin">Admin</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

// User Modal Component
const UserModal = ({
  user,
  organizations = [],
  onSave,
  onClose,
  canCreateAdmin = false,
  canEditPrivileges = false,
  canCreateOrganization = false,
  pickedOrganization = '',
  onRequestNewOrganization,
}) => {
  const isEdit = !!user;
  const [usernameTouched, setUsernameTouched] = useState(isEdit);
  const [passwordIsTemporary, setPasswordIsTemporary] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [formData, setFormData] = useState({
    username: user?.username || '',
    email: user?.email || (looksLikeEmail(user?.username) ? user.username : ''),
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    organization: user?.organization || '',
    password: '',
    role: openingRole(user, canCreateAdmin),
    aiRole: user?.aiRole || 'enabled',
    resellerAssetsEditor: user?.resellerAssetsEditor === true,
  });
  const [showPassword, setShowPassword] = useState(false);
  const canHoldAssetsEditor = ['sales', 'user'].includes(formData.role);
  const orgOptions = organisationChoices(organizations, (user && user.organization) || pickedOrganization);

  useEffect(() => {
    if (!pickedOrganization) return;
    setFormData((current) => ({ ...current, organization: pickedOrganization }));
  }, [pickedOrganization]);

  useEffect(() => {
    setFormData((current) => {
      const match = organisationChoices(organizations, current.organization)
        .find((name) => name.toLowerCase() === String(current.organization || '').toLowerCase());
      if (!match || match === current.organization) return current;
      return { ...current, organization: match };
    });
  }, [organizations]);

  const updateField = (name, value) => {
    setFormData((current) => {
      const next = { ...current, [name]: value };
      if (!isEdit && name === 'email' && !usernameTouched) {
        const localPart = String(value || '').split('@')[0] || '';
        next.username = localPart.toLowerCase().replace(/[^a-z0-9._-]/g, '');
      }
      return next;
    });
  };

  const fillTemporaryPassword = () => {
    setPasswordIsTemporary(true);
    setShowPassword(true);
    setFormData((current) => ({ ...current, password: createTemporaryPassword() }));
  };

  const copyPassword = async () => {
    if (!formData.password) return;
    try {
      await navigator.clipboard.writeText(formData.password);
    } catch (error) {
      // The password stays visible in the field so it can still be copied by hand.
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveError('');
    if (!String(formData.firstName || '').trim() || !String(formData.lastName || '').trim() || !String(formData.email || '').trim()) {
      setSaveError('First name, last name, and email are required.');
      return;
    }
    if (!formData.organization) {
      setSaveError('Choose an organisation.');
      return;
    }
    const submitData = { ...formData };

    if (isEdit) {
      delete submitData.username;
      if (!canEditPrivileges || !formData.password) {
        delete submitData.password;
        delete submitData.requirePasswordChange;
      } else if (passwordIsTemporary) {
        submitData.requirePasswordChange = true;
      } else {
        delete submitData.requirePasswordChange;
      }
      if (!canEditPrivileges) {
        delete submitData.role;
        delete submitData.aiRole;
        delete submitData.resellerAssetsEditor;
      } else if (!canHoldAssetsEditor) {
        submitData.resellerAssetsEditor = false;
      }
    } else {
      delete submitData.resellerAssetsEditor;
      if (passwordIsTemporary) submitData.requirePasswordChange = true;
    }

    const result = await onSave(submitData);
    if (result && result.success === false) {
      setSaveError(result.error || 'Could not save these changes.');
    }
  };

  return (
    <div className="rf-modal-overlay">
      <div className="rf-modal">
        <div className="rf-modal-header">
          <h3>
            <i className={`fas fa-${isEdit ? 'user-edit' : 'user-plus'}`}></i>
            {isEdit ? `Edit User: ${displayUserName(user)}` : 'Create New User'}
          </h3>
          <button className="rf-modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <form onSubmit={handleSubmit} noValidate>
          <div className="rf-modal-body">
            <div className="rf-form-row">
              <Input
                label="First name"
                name="firstName"
                value={formData.firstName}
                onChange={(e) => updateField('firstName', e.target.value)}
                required
                placeholder="Jane"
              />
              <Input
                label="Last name"
                name="lastName"
                value={formData.lastName}
                onChange={(e) => updateField('lastName', e.target.value)}
                required
                placeholder="Smith"
              />
            </div>

            <Input
              label="Email"
              type="email"
              name="email"
              value={formData.email}
              onChange={(e) => updateField('email', e.target.value)}
              required
              placeholder="jane.smith@example.com"
            />
            <small className="rf-help-text">They can sign in with this address</small>

            <div className="rf-form-group">
              <label htmlFor="user-organization" className="rf-label">
                Organisation
                <span style={{ color: 'var(--rf-danger)' }}> *</span>
              </label>
              <select
                id="user-organization"
                name="organization"
                value={formData.organization}
                onChange={(e) => updateField('organization', e.target.value)}
                className="rf-select"
                required
              >
                <option value="">Select an organisation</option>
                {orgOptions.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
              {canCreateOrganization ? (
                <div className="rf-inline-actions">
                  <Button type="button" variant="outline" size="sm" onClick={onRequestNewOrganization}>
                    <i className="fas fa-plus"></i> New organisation
                  </Button>
                </div>
              ) : (
                <small className="rf-help-text">Choose from the organisations already on the list.</small>
              )}
            </div>

            <Input
              label="Handle"
              name="username"
              value={formData.username}
              onChange={(e) => {
                if (isEdit) return;
                setUsernameTouched(true);
                updateField('username', e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, ''));
              }}
              disabled={isEdit}
              placeholder="Filled from the email if you leave this blank"
            />
            <small className="rf-help-text">
              {isEdit ? 'Login name for this account' : 'Login name. Leave blank and we use the email name.'}
            </small>

            {( !isEdit || canEditPrivileges ) && (
            <div className="rf-form-group">
              <label className="rf-input-label" htmlFor="user-password">
                {isEdit ? 'New password (leave blank to keep the current one)' : 'Password'}
              </label>
              <div className="rf-password-input">
                <input
                  id="user-password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => {
                    setPasswordIsTemporary(false);
                    setFormData({ ...formData, password: e.target.value });
                  }}
                  placeholder={isEdit ? 'Enter new password' : 'Enter password'}
                  className="rf-input"
                  required={!isEdit}
                  minLength={4}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="rf-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <i className={`fas fa-${showPassword ? 'eye-slash' : 'eye'}`}></i>
                </button>
              </div>
              <div className="rf-inline-actions">
                <Button type="button" variant="outline" size="sm" onClick={fillTemporaryPassword}>
                  Create temporary password
                </Button>
                {formData.password && (
                  <Button type="button" variant="outline" size="sm" onClick={copyPassword}>
                    Copy password
                  </Button>
                )}
              </div>
              <small className="rf-help-text">
                {passwordIsTemporary
                  ? 'They must change this the first time they sign in. Copy it before you close this screen.'
                  : 'Minimum 4 characters'}
              </small>
            </div>
            )}

            {(!isEdit || canEditPrivileges) && (
            <div className="rf-form-row">
              <div className="rf-form-group">
                <label className="rf-input-label" htmlFor="user-role">Role</label>
                <select
                  id="user-role"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="rf-select"
                >
                  {roleChoices({
                    canCreateAdmin,
                    includeGuest: Boolean(user && user.role === 'client'),
                  }).map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                {isEdit && user.role === 'client' && formData.role === 'external' ? (
                  <small className="rf-help-text">
                    This login was created for Design Check. Saving makes them a customer.
                  </small>
                ) : isInternalRole(formData.role) && !isInternalEmail(formData.email) ? (
                  <small className="rf-help-text rf-role-warning">
                    <i className="fas fa-exclamation-triangle"></i> This is a Rapid Fusion role but the email is not @{INTERNAL_EMAIL_DOMAIN}. You will be asked to confirm.
                  </small>
                ) : (
                  <small className="rf-help-text">
                    External is for customers. Rapid Fusion roles are for @{INTERNAL_EMAIL_DOMAIN} staff.
                  </small>
                )}
              </div>
              <div className="rf-form-group">
                <label className="rf-input-label">AI Access</label>
                <select
                  value={formData.aiRole}
                  onChange={(e) => setFormData({ ...formData, aiRole: e.target.value })}
                  className="rf-select"
                >
                  <option value="enabled">Enabled</option>
                  <option value="disabled">Disabled</option>
                  <option value="admin">Admin</option>
                </select>
                <small className="rf-help-text">
                  Controls AI assistant access
                </small>
              </div>
            </div>
            )}

            {isEdit && canEditPrivileges && canHoldAssetsEditor && (
            <div className="rf-form-group">
              <label className="rf-input-label">
                <input
                  type="checkbox"
                  checked={formData.resellerAssetsEditor}
                  onChange={(e) => setFormData({ ...formData, resellerAssetsEditor: e.target.checked })}
                />{' '}
                Can edit Reseller Assets
              </label>
              <small className="rf-help-text">
                Upload, rename, replace and delete files and folders in Reseller Assets (HubSpot). Admins can always edit.
              </small>
            </div>
            )}

          </div>
          <div className="rf-modal-footer">
            {saveError && <p className="rf-help-text rf-role-warning" role="alert">{saveError}</p>}
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="primary">
              <i className={`fas fa-${isEdit ? 'save' : 'plus'}`}></i>
              {isEdit ? 'Save Changes' : 'Create User'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AccessManagement;
